<template>
  <b-modal
    :id="modalId"
    :title="operatorName"
    size="lg"
    hide-footer
    centered
    @show="fetchTrackingLogs"
  >
    <div class="tracking-container">
      <div
        v-if="isLoading"
        class="text-center py-4"
      >
        <b-spinner variant="primary" />
        <p class="mt-2">
          {{ $t('general.loading') }}
        </p>
      </div>
      <div v-else>
        <div class="step-list">
          <div
            v-for="(step, index) in detailTrackingList"
            :key="index"
            class="step-card position-relative"
          >
            <!-- Step Number Badge -->
            <div
              class="step-number"
              :class="`bg-${step.status}`"
            >
              <span>{{ index + 1 }}</span>
            </div>

            <!-- Vertical Line -->
            <div
              v-if="index < (detailTrackingList || []).length - 1"
              class="step-connector"
            />
            <b-row>
              <b-col>
                <h6 :class="step.status">
                  {{ $t(`status.${step.status}`) }}
                </h6>
                <p
                  v-if="step.status === 'replaced'"
                  v-html="getStatusDescription('replaced', step.transfer.fromOperator, step.transfer.fromOperatorId, step.transfer.toOperator, step.transfer.toOperatorId, step.status)"
                />
                <p 
                  v-else-if="step.status === 'pending' && step.isAutoAssign"
                  v-html="getStatusDescription('pendingSystem', step.assignedOperator, step.assignOperatorId, step.status)"
                />
                <p
                  v-else-if="step.status === 'pending' && step.assignedBy"
                  v-html="getStatusDescription('pendingHuman', step.assignedBy.name, step.assignedBy.id, step.assignedOperator, step.assignOperatorId, step.status)"
                />
                <p
                  v-else-if="step.status === 'pending' && !step.isAutoAssign && !step.assignedBy"
                  v-html="getStatusDescription('pendingHuman', step.assignedOperator, step.assignOperatorId, step.status)"
                />
                <p
                  v-else-if="step.status === 'cancel' && step.participant.participantType === 'customer'"
                  v-html="$t('statusDescription.cancelledByCustomer', { operator: step.participant.name })"
                />
                <p
                  v-else-if="step.status === 'cancel' && step.participant.participantType === 'cronJob'"
                  v-html="$t('statusDescription.cancelSystem')"
                />
                <p
                  v-else-if="step.status === 'assign' && !step.isAutoAssign"
                  v-html="getStatusDescription('pending', step.participant.name, step.assignOperatorId, step.status)"
                />
                <div
                  v-else-if="step.status === 'pending'"
                  class="mb-1"
                  v-html="getStatusDescription('pending', step.assignedOperator, step.assignOperatorId, step.status)"
                />
                <p
                  v-else-if="step.status === 'deleted'"
                  class="mb-1"
                  v-html="getStatusDescription('deleted', step.transfer.fromOperator, step.transfer.fromOperatorId, step.transfer.toOperator, step.transfer.toOperatorId, step.meta.currentStatus)"
                />
                <p
                  v-else
                  class="mb-1"
                  v-html="getStatusDescription(step.status, step.assignedOperator, step.assignOperatorId, step.status)"
                />
                <div class="detail-group">
                  <label>{{ step.createdAt | formatDate }}</label>
                </div>
                <div v-if="step.status === 'completed' || step.status === 'cancel' || step.status === 'hold'">
                  <div>
                    <template v-if="step.status === 'completed' && !step.isFromHelper && !step.isPartialCompleted && step.servicePrice !== null">
                      <strong>{{ $t('field.servicePrice') }}: </strong>
                      {{ !!step.servicePrice || step.servicePrice === 0 ? formatNumber(step.servicePrice, step.servicePriceCurrency) + ' ' + (step.servicePriceCurrency || '') : $t('general.not_available') }}
                      <br>
                    </template>
                    <div style="margin-top: 4px;" v-if="step.status === 'completed' && !step.isFromHelper && !step.isPartialCompleted && !!step.servicePriceInit">
                      <strong>{{ $t('field.originalPrice') }}: </strong>
                      {{ !!step.servicePriceInit || step.servicePriceInit === 0 ? formatNumber(step.servicePriceInit) + ' ' + (step.servicePriceCurrency || '') : $t('general.not_available') }}
                      <br>
                    </div>
                  </div>
                </div>
                <div
                  v-if="(step.status === 'cancel' || step.status === 'hold' || step.status === 'completed') && step.remark"
                  class="mt-1"
                >
                  <div>
                    <strong>{{ $t('field.remark') }}: </strong>
                    <span>{{ step.remark || $t('general.not_available') }}</span>
                    <b-button
                      class="btn-translate"
                      v-b-tooltip.hover
                      :title="!remarkLoading[`step_${index}_remark`]
                        ? (remarkTranslated[`step_${index}_remark`] ? $t('chat.revert') : $t('chat.translate'))
                        : ''"
                      variant="light"
                      pill
                      style="
                        height: fit-content;
                        width: fit-content;
                        padding: 4px 8px;
                        margin-right: 0.5rem;
                        margin-left: 4px;
                      "
                      size="sm"
                      @click="remarkTranslated[`step_${index}_remark`]
                        ? revertRemark(index)
                        : translateRemark(index, step.remark)"
                    >
                      <b-spinner v-if="remarkLoading[`step_${index}_remark`]" style="width: 14px; height: 14px;" label="loading"></b-spinner>
                      <feather-icon v-else-if="remarkTranslated[`step_${index}_remark`]" icon="CornerUpLeftIcon" style="width: 14px; height: 14px;" />
                      <translate-icon v-else style="width: 13px; height: 13px;" />
                    </b-button>
                  </div>
                  <div>
                    <div
                      v-if="step.remarkMediaIds && step.remarkMediaIds.length"
                      class="file-list mt-1"
                    >
                      <div
                        v-for="({url, name}, idx) in getVisibleFiles(step.remarkMediaIds)"
                        :key="url + idx"
                        class="file-item d-flex align-items-center"
                      >
                        <div
                          class="file-preview cursor-pointer"
                          @click="showMediaPreview(url, getVisibleFiles(step.remarkMediaIds, false))"
                        >
                          <img
                            v-if="isImageFile(url)"
                            :src="url"
                            alt="preview"
                          >
                          <div
                            v-else
                            :style="{ padding: '8px 3px', position: 'relative' }"
                          >
                            <file-icon :style="{color: 'gray'}" />
                            <span
                              class="file-ext"
                              :style="{ color: 'white' }"
                            >
                              {{ getFileExtDisplay(url) }}
                            </span>
                          </div>
                        </div>
                        <span
                          class="ml-2 file-name cursor-pointer text-truncate"
                          style="width: 400px"
                          @click="showMediaPreview(url, getVisibleFiles(step.remarkMediaIds, false))"
                        >
                          {{ name }}
                        </span>
                      </div>

                      <div
                        v-if="step.remarkMediaIds && step.remarkMediaIds.length > 5 && !showAll"
                        class="show-more-button mt-2 cursor-pointer"
                        @click="showAll = true"
                      >
                        {{ $t('general.showAllFiles', { count: step.remarkMediaIds.length - 5 }) }}
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>
        </div>
        <div
          v-if="!detailTrackingList.length"
          class="no-data-text text-center py-4"
        >
          {{ $t('general.noData') }}
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="text-right mt-1">
      <b-button
        variant="outline-secondary"
        class="px-3"
        @click="closeModal"
      >
        {{ $t('button.close') }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BRow, BCol, BSpinner,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import FileIcon from '@/components/icons/FileIcon.vue'
import TranslateIcon from '@/components/icons/TranslateIcon.vue'
import { formatNumber } from '@/@core/utils/utils'
import { isImage, getFileExtDisplay } from '@/helpers/fileHelper'
import { camelize } from 'humps'

const ServiceSystemRepository = Repository.get('serviceSystem')
const TranslateRepository = Repository.get('translateRepository')

export default {
  name: 'OperatorTrackerModal',
  components: {
    BModal,
    BButton,
    BRow,
    BCol,
    FileIcon,
    BSpinner,
    TranslateIcon,
  },
  props: {
    modalId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      detailTrackingList: [],
      isLoading: false,
      operatorId: null,
      serviceId: null,
      showAll: false,
      operatorName: '',
      // Translation states
      remarkTranslate: {},
      remarkLoading: {},
      remarkOriginal: {},
      remarkTranslated: {},
      camelize,
    }
  },
  methods: {
    show(id, serviceId, operatorName) {
      this.operatorId = id
      this.serviceId = serviceId
      this.operatorName = operatorName
      this.$bvModal.show(this.modalId)
      this.remarkLoading = {}
      this.remarkTranslate = {}
      this.remarkOriginal = {}
      this.remarkTranslated = {}
    },
    closeModal() {
      this.$bvModal.hide(this.modalId)
    },
    formatDateTime(date) {
      if (!date) return ''
      return new Date(date).toLocaleString()
    },
    formatNumber,
    async fetchTrackingLogs() {
      try {
        this.isLoading = true
        const response = await ServiceSystemRepository.logs(
          {
            assignedOperatorId: this.operatorId,
          },
          this.serviceId,
        )

        if (response?.data?.data) {
          this.detailTrackingList = (response.data.data.list || []).reverse()
        }
      } catch (error) {
        console.error('Error fetching tracking logs:', error)
      } finally {
        this.isLoading = false
      }
    },
    getStatusDescription(status, operator, operatorId, newOperator, newOperatorId, currentStatus = null) {
      // Get the translation template
      const template = this.$t(`statusDescription.${status}`)

      // First replace new operator placeholder
      let result = template.replace(
        '<strong>{newOperator}</strong>',
        `<strong class="operator-link" onclick="window.routeToOperator('${newOperatorId}')">${newOperator}</strong>`,
      )

      // Then replace original operator placeholder
      result = result.replace(
        '<strong>{operator}</strong>',
        `<strong class="operator-link" onclick="window.routeToOperator('${operatorId}')">${operator}</strong>`,
      )

      result = result.replace(
        '<strong>{operator1}</strong>',
        `<strong class="operator-link" onclick="window.routeToOperator('${operatorId}')">${operator}</strong>`,
      )

      result = result.replace(
        '<strong>{operator2}</strong>',
        `<strong class="operator-link" onclick="window.routeToOperator('${newOperatorId}')">${newOperator}</strong>`,
      )

      if (status === 'deleted') {
        const statusText = this.$t(`status.${currentStatus}`)
        result = result.replace(
          '<strong>{status}</strong>',
          `<strong class="${currentStatus}">${statusText}</strong>`,
        )
      }

      return result
    },
    isImageFile(url) {
      return isImage(url)
    },
    getFileExtDisplay,
    getVisibleFiles(remarkMediaIds, showAll = true) {
      if (!remarkMediaIds) return []
      const files = remarkMediaIds.map(media => ({ url: media.url, name: media.fileName }))
      return showAll ? files : files.slice(0, 5)
    },
    showMediaPreview(url, files) {
      if (!files || !Array.isArray(files)) return
      window.open(url, '_blank')
    },
    translateRemark(stepIndex, remark) {
      if (!remark) return
      const key = `step_${stepIndex}_remark`
      if (this.remarkLoading[key]) return
      
      this.$set(this.remarkLoading, key, true)
      const target = this.$i18n.locale

      TranslateRepository.translate({ text: remark, target })
        .then(response => {
          const translated = response?.data?.data?.translatedMessage || remark
          this.$set(this.remarkTranslate, key, translated)

          // Save original only once
          if (!(key in this.remarkOriginal)) {
            this.$set(this.remarkOriginal, key, remark)
          }

          // Update the remark in the tracking list
          this.$set(this.detailTrackingList[stepIndex], 'remark', translated)
          this.$set(this.remarkTranslated, key, true)
          this.$set(this.remarkLoading, key, false)
        })
        .catch(() => {
          this.$set(this.remarkLoading, key, false)
        })
    },
    revertRemark(stepIndex) {
      const key = `step_${stepIndex}_remark`
      if (this.remarkLoading[key]) return
      
      const original = this.remarkOriginal[key]
      if (original == null) return

      // Restore original remark
      this.$set(this.detailTrackingList[stepIndex], 'remark', original)
      this.$delete(this.remarkTranslate, key)
      this.$set(this.remarkTranslated, key, false)
    },
  },
}
</script>

<style lang="scss" scoped>
.tracking-container {
  max-height: 60vh;
  overflow-y: auto;
  padding: 1rem;
}

.step-list {
  position: relative;
  padding-left: 40px;
}

.step-card {
  background-color: #F7F7F7;
  border-radius: 8px;
  padding: 15px;
  box-shadow: none;
  margin-bottom: 14px;
  position: relative;
}

.step-connector {
  position: absolute;
  left: -25px;
  top: 30px;
  width: 2px;
  height: calc(100% + 20px);
  background-color: #dee2e6;
  z-index: 1;
}

.step-number {
  position: absolute;
  left: -40px;
  top: 8px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  z-index: 2;
}

.detail-group {
  label {
    font-weight: 600;
    color: #6e6b7b;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }

  p {
    color: #5e5873;
  }
}

// Status Colors
.pending {
  color: #ffc107;
}

.bg-pending {
  background-color: #ffc107;
}

.processing {
  color: rgb(0, 123, 255);
}

.bg-processing {
  background-color: rgb(0, 123, 255);
}

.cancel {
  color: #c0392b;
}

.bg-cancel {
  background-color: #c0392b;
}

.unresolved {
  color: #ff8c00;
}

.bg-unresolved {
  background-color: #ff8c00;
}

.hold {
  color: #8d5185;
}

.bg-hold {
  background-color: #8d5185;
}

.completed {
  color: #27ae60;
}

.resolved {
  color: #7e7e7e;
}

.bg-completed {
  background-color: #27ae60;
}

.assign {
  color: #97b834;
}

.bg-assign {
  background-color: #97b834;
}

.bg-resolved {
  background-color: #7e7e7e;
}

.bg-handover {
  background-color: var(--primary);
}

.handover {
  color: var(--primary);
}

.rejected {
  color: #e74c3c;
}

.bg-rejected {
  background-color: #e74c3c;
}

.bg-accepted {
  background-color: #2ecc71;
}

.accepted {
  color: #2ecc71;
}

.pendingPrice {
  color: #f1c40f;
}

.bg-pendingPrice {
  background-color: #f1c40f;
}

.assignPrice {
    color: #2196f3;
  }

  .bg-assignPrice {
    background-color: #2196f3;
  }

// File Preview Styles
.file-preview {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.file-item {
  border: 1px solid #eee;
  border-radius: 4px;
  margin-bottom: 8px;
  align-items: center;
  padding: 4px 8px;
}

.replaced {
    color: #ff8c00;
  }

  .bg-replaced {
    background-color: #ff8c00;
  }

.file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 8px;
  flex: 1;

  &:hover {
    color: #7367f0;
    text-decoration: underline;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.no-data-text {
  color: #b9b9c3;
  font-style: italic;
  font-size: 0.857rem;
}

.pendingDispatch {
  color: #f39c12; /* New color for pending dispatch */
}

.bg-pendingDispatch {
  background-color: #f39c12; /* New color for pending dispatch */
}

.deleted {
    color: #e74c3c;
  }

  .bg-deleted {
    background-color: #e74c3c;
  }

.btn-translate.btn-translate.btn:focus{
  outline: none;
}

.btn-translate.btn-translate.btn:active{
  background-color: #f4f0f8;
  border-color: #f4f0f8;
}
</style>
